import type { Span, Tracer } from "@opentelemetry/api"
import type { Logger, SeverityNumber } from "@opentelemetry/api-logs"
import type { LoggerProvider } from "@opentelemetry/sdk-logs"
import type { MeterProvider } from "@opentelemetry/sdk-metrics"
import type { BasicTracerProvider } from "@opentelemetry/sdk-trace-base"

export type TelemetryAttributeValue = string | number | boolean
export type TelemetryAttributes = Readonly<Record<string, TelemetryAttributeValue | undefined>>
export type TelemetryLogSeverity = "debug" | "info" | "warn" | "error"

export interface TelemetryLogRecord {
  readonly severity: TelemetryLogSeverity
  readonly message: string
  readonly attributes: TelemetryAttributes
}

export interface TelemetryRedactionPolicy {
  /** May rewrite a structured log record or return null to suppress it. */
  redactLog(record: Readonly<TelemetryLogRecord>): TelemetryLogRecord | null
}

export interface OtlpTelemetryOptions {
  /** Base OTLP/HTTP URL. /v1/logs, /v1/traces and /v1/metrics are appended. */
  readonly baseUrl: string
  readonly headers?: Readonly<Record<string, string>>
  readonly logs?: boolean
  readonly traces?: boolean
  readonly metrics?: boolean
  readonly metricExportIntervalMs?: number
}

export interface ApplicationTelemetryOptions {
  readonly serviceName?: string
  readonly serviceVersion?: string
  readonly environment?: string
  /** Structured records are written to the browser/devtools console. Defaults to true. */
  readonly console?: boolean
  /** Enables local-only debug records for this application runtime. Never persisted. */
  readonly debug?: boolean
  /** Captures uncaught browser errors and unhandled promise rejections. Defaults to true. */
  readonly captureGlobalErrors?: boolean
  /** All structured log records pass through this hook before local or remote emission. */
  readonly redactionPolicy?: TelemetryRedactionPolicy
  readonly slowQueryThresholdMs?: number | false
  readonly slowCommandThresholdMs?: number | false
  /** Optional remote OTLP/HTTP destination such as Cloud.Server or an OTel Collector. */
  readonly otlp?: OtlpTelemetryOptions
}

export interface ApplicationTelemetry {
  /** Resolves after optional remote OpenTelemetry providers finish lazy initialization. */
  readonly ready: Promise<void>
  readonly debugEnabled: boolean
  setDebugEnabled(enabled: boolean): void
  /** Local-only and emitted only while debug is enabled. Never sent through OTLP. */
  debug(message: string, attributes?: TelemetryAttributes): void
  info(message: string, attributes?: TelemetryAttributes): void
  warn(message: string, attributes?: TelemetryAttributes): void
  error(message: string, error?: unknown, attributes?: TelemetryAttributes): void
  reportError(error: unknown, attributes?: TelemetryAttributes): void
  forceFlush(): Promise<void>
  shutdown(): Promise<void>
}

export type RemoteTelemetryOperationKind = "query" | "command"
export type RemoteTelemetryOperationOutcome = "success" | "error" | "cancelled"

export interface RemoteTelemetryOperation {
  excludeDuration(durationMs: number): void
  end(outcome: RemoteTelemetryOperationOutcome, error?: unknown): number
}

/** @internal */
export interface RemoteTelemetryRequest {
  end(outcome: RemoteTelemetryOperationOutcome, status?: number): void
}

/** @internal */
export interface ApplicationTelemetryRuntime {
  readonly telemetry: ApplicationTelemetry
  startRemoteOperation(kind: RemoteTelemetryOperationKind, name: string): RemoteTelemetryOperation
  startRemoteRequest(
    kind: RemoteTelemetryOperationKind,
    name: string,
    method: string,
    headers: Headers
  ): RemoteTelemetryRequest | undefined
}

const instrumentationName = "@florisoft/framework"
const instrumentationVersion = "0.1.0"
const severityNumber = {
  debug: 5 as SeverityNumber,
  info: 9 as SeverityNumber,
  warn: 13 as SeverityNumber,
  error: 17 as SeverityNumber,
} as const

const passThroughRedactionPolicy: TelemetryRedactionPolicy = Object.freeze({
  redactLog(record: Readonly<TelemetryLogRecord>) {
    return record
  },
})

interface RemoteOtlpRuntime {
  readonly logger?: Logger
  readonly loggerProvider?: LoggerProvider
  readonly tracer?: Tracer
  readonly traceProvider?: BasicTracerProvider
  readonly meterProvider?: MeterProvider
  recordOperation(event: CompletedRemoteOperation): void
  startRequest(
    kind: RemoteTelemetryOperationKind,
    name: string,
    method: string,
    headers: Headers
  ): RemoteTelemetryRequest | undefined
}

interface CompletedRemoteOperation {
  readonly kind: RemoteTelemetryOperationKind
  readonly name: string
  readonly outcome: RemoteTelemetryOperationOutcome
  readonly attributes: TelemetryAttributes
  readonly effectiveDurationMs: number
}

export function createApplicationTelemetryRuntime(
  options: ApplicationTelemetryOptions = {}
): ApplicationTelemetryRuntime {
  const slowQueryThresholdMs = normalizeSlowThreshold(options.slowQueryThresholdMs, 2_000)
  const slowCommandThresholdMs = normalizeSlowThreshold(options.slowCommandThresholdMs, 3_000)
  const redactionPolicy = options.redactionPolicy ?? passThroughRedactionPolicy
  const remoteTracingEnabled = options.otlp != null && options.otlp.traces !== false
  const resourceAttributes = {
    "service.name": options.serviceName ?? "florisoft-application",
    ...(options.serviceVersion == null ? {} : { "service.version": options.serviceVersion }),
    ...(options.environment == null ? {} : { "deployment.environment.name": options.environment }),
  } satisfies TelemetryAttributes

  let debugEnabled = options.debug ?? false
  let isShutdown = false
  let removeGlobalErrorCapture: () => void = () => undefined
  let remoteRuntime: Promise<RemoteOtlpRuntime | undefined> | undefined
  let resolvedRemoteRuntime: RemoteOtlpRuntime | undefined
  let ready: Promise<void> = Promise.resolve()

  function redact(record: TelemetryLogRecord): TelemetryLogRecord | null {
    try {
      return redactionPolicy.redactLog(record)
    } catch (error) {
      if (options.console !== false) {
        console.warn("Telemetry redaction policy failed; the record was suppressed.", error)
      }
      return null
    }
  }

  function createLogRecord(
    severity: TelemetryLogSeverity,
    message: string,
    attributes?: TelemetryAttributes
  ): TelemetryLogRecord | null {
    return redact(
      Object.freeze({
        severity,
        message,
        attributes: Object.freeze(sanitizeAttributes(attributes) ?? {}),
      })
    )
  }

  function emitLocal(record: TelemetryLogRecord) {
    if (options.console === false || isShutdown) return
    const output = {
      severityNumber: severityNumber[record.severity],
      severityText: record.severity.toUpperCase(),
      body: record.message,
      attributes: formatConsoleAttributes(record.attributes),
    }
    if (record.severity === "error") console.error(output)
    else if (record.severity === "warn") console.warn(output)
    else if (record.severity === "info") console.info(output)
    else console.debug(output)
  }

  function emitRemote(record: TelemetryLogRecord) {
    if (isShutdown || remoteRuntime == null) return
    void remoteRuntime.then((runtime) => {
      if (runtime == null || isShutdown) return
      runtime.logger?.emit({
        severityNumber: severityNumber[record.severity],
        severityText: record.severity.toUpperCase(),
        body: record.message,
        attributes: sanitizeAttributes(record.attributes),
      })
    })
  }

  function emit(severity: TelemetryLogSeverity, message: string, attributes?: TelemetryAttributes, remote = true) {
    const record = createLogRecord(severity, message, attributes)
    if (record == null) return
    emitLocal(record)
    if (remote) emitRemote(record)
  }

  const telemetry: ApplicationTelemetry = Object.freeze({
    get ready() {
      return ready
    },
    get debugEnabled() {
      return debugEnabled
    },
    setDebugEnabled(enabled: boolean) {
      debugEnabled = enabled
    },
    debug(message: string, attributes?: TelemetryAttributes) {
      if (!debugEnabled) return
      emit("debug", message, attributes, false)
    },
    info(message: string, attributes?: TelemetryAttributes) {
      emit("info", message, attributes)
    },
    warn(message: string, attributes?: TelemetryAttributes) {
      emit("warn", message, attributes)
    },
    error(message: string, error?: unknown, attributes?: TelemetryAttributes) {
      emit("error", message, {
        ...attributes,
        ...(error === undefined ? {} : describeError(error)),
      })
    },
    reportError(error: unknown, attributes?: TelemetryAttributes) {
      emit("error", "Unexpected application error", {
        ...attributes,
        ...describeError(error),
      })
    },
    async forceFlush() {
      const remote = await remoteRuntime
      await Promise.all([
        remote?.loggerProvider?.forceFlush(),
        remote?.traceProvider?.forceFlush(),
        remote?.meterProvider?.forceFlush(),
      ])
    },
    async shutdown() {
      if (isShutdown) return
      isShutdown = true
      removeGlobalErrorCapture()
      removeGlobalErrorCapture = () => undefined
      const remote = await remoteRuntime
      await Promise.all([
        remote?.loggerProvider?.shutdown(),
        remote?.traceProvider?.shutdown(),
        remote?.meterProvider?.shutdown(),
      ])
    },
  })

  if (options.captureGlobalErrors !== false && typeof window !== "undefined") {
    removeGlobalErrorCapture = installGlobalErrorCapture(telemetry)
  }

  const otlp = options.otlp
  if (otlp != null) {
    const otlpBaseUrl = normalizeOtlpBaseUrl(otlp.baseUrl)
    remoteRuntime = createRemoteOtlpRuntime(resourceAttributes, otlpBaseUrl, otlp)
      .then((runtime) => {
        if (!isShutdown) resolvedRemoteRuntime = runtime
        return runtime
      })
      .catch((error) => {
        emit("warn", "Remote telemetry initialization failed", describeError(error), false)
        return undefined
      })
    ready = remoteRuntime.then(() => undefined)
  }

  return Object.freeze({
    telemetry,
    startRemoteOperation(kind: RemoteTelemetryOperationKind, name: string) {
      return createRemoteOperation({
        kind,
        name,
        remoteRuntime,
        telemetry,
        slowThresholdMs: kind === "query" ? slowQueryThresholdMs : slowCommandThresholdMs,
      })
    },
    startRemoteRequest(kind: RemoteTelemetryOperationKind, name: string, method: string, headers: Headers) {
      if (isShutdown || !remoteTracingEnabled) return undefined
      return resolvedRemoteRuntime?.startRequest(kind, name, method, headers)
    },
  })
}

function createRemoteOperation(options: {
  readonly kind: RemoteTelemetryOperationKind
  readonly name: string
  readonly remoteRuntime?: Promise<RemoteOtlpRuntime | undefined>
  readonly telemetry: ApplicationTelemetry
  readonly slowThresholdMs: number | false
}): RemoteTelemetryOperation {
  const startedAt = now()
  let excludedDurationMs = 0
  let ended = false

  return {
    excludeDuration(durationMs) {
      if (!ended && Number.isFinite(durationMs) && durationMs > 0) {
        excludedDurationMs += durationMs
      }
    },
    end(outcome, error) {
      if (ended) return 0
      ended = true
      const durationMs = Math.max(0, now() - startedAt - excludedDurationMs)
      const attributes = {
        "florisoft.remote.kind": options.kind,
        "florisoft.remote.name": options.name,
        "florisoft.remote.outcome": outcome,
        "florisoft.remote.duration_ms": durationMs,
        ...(excludedDurationMs > 0 ? { "florisoft.remote.excluded_wait_ms": excludedDurationMs } : {}),
        ...(outcome === "error" ? describeErrorType(error) : {}),
      } satisfies TelemetryAttributes

      if (options.remoteRuntime != null) {
        void options.remoteRuntime.then((runtime) =>
          runtime?.recordOperation({
            kind: options.kind,
            name: options.name,
            outcome,
            attributes,
            effectiveDurationMs: durationMs,
          })
        )
      }

      options.telemetry.debug(`Remote ${options.kind} completed`, attributes)
      if (options.slowThresholdMs !== false && outcome !== "cancelled" && durationMs >= options.slowThresholdMs) {
        options.telemetry.warn(`Slow remote ${options.kind}`, {
          ...attributes,
          "florisoft.remote.slow_threshold_ms": options.slowThresholdMs,
        })
      }
      return durationMs
    },
  }
}

async function createRemoteOtlpRuntime(
  resourceAttributes: TelemetryAttributes,
  baseUrl: string,
  options: OtlpTelemetryOptions
): Promise<RemoteOtlpRuntime> {
  const headers = options.headers == null ? undefined : { ...options.headers }
  const [
    logExporterModule,
    traceExporterModule,
    metricExporterModule,
    traceSdkModule,
    metricSdkModule,
    logsSdkModule,
    resourcesModule,
    apiModule,
  ] = await Promise.all([
    options.logs === false ? undefined : import("@opentelemetry/exporter-logs-otlp-http"),
    options.traces === false ? undefined : import("@opentelemetry/exporter-trace-otlp-http"),
    options.metrics === false ? undefined : import("@opentelemetry/exporter-metrics-otlp-http"),
    options.traces === false ? undefined : import("@opentelemetry/sdk-trace-base"),
    options.metrics === false ? undefined : import("@opentelemetry/sdk-metrics"),
    options.logs === false ? undefined : import("@opentelemetry/sdk-logs"),
    import("@opentelemetry/resources"),
    import("@opentelemetry/api"),
  ])
  const resource = resourcesModule.resourceFromAttributes(sanitizeAttributes(resourceAttributes) ?? {})

  const loggerProvider =
    logExporterModule == null || logsSdkModule == null
      ? undefined
      : new logsSdkModule.LoggerProvider({
          resource,
          processors: [
            new logsSdkModule.BatchLogRecordProcessor({
              exporter: new logExporterModule.OTLPLogExporter({
                url: `${baseUrl}/v1/logs`,
                headers,
              }),
            }),
          ],
        })
  const logger = loggerProvider?.getLogger(instrumentationName, instrumentationVersion)

  const traceProvider =
    traceExporterModule == null || traceSdkModule == null
      ? undefined
      : new traceSdkModule.BasicTracerProvider({
          resource,
          spanProcessors: [
            new traceSdkModule.BatchSpanProcessor(
              new traceExporterModule.OTLPTraceExporter({
                url: `${baseUrl}/v1/traces`,
                headers,
              })
            ),
          ],
        })
  const tracer = traceProvider?.getTracer(instrumentationName, instrumentationVersion)

  const meterProvider =
    metricExporterModule == null || metricSdkModule == null
      ? undefined
      : new metricSdkModule.MeterProvider({
          resource,
          readers: [
            new metricSdkModule.PeriodicExportingMetricReader({
              exporter: new metricExporterModule.OTLPMetricExporter({
                url: `${baseUrl}/v1/metrics`,
                headers,
              }),
              exportIntervalMillis: options.metricExportIntervalMs ?? 60_000,
            }),
          ],
        })
  const meter = meterProvider?.getMeter(instrumentationName, instrumentationVersion)
  const durationHistogram = meter?.createHistogram("florisoft.remote.operation.duration", {
    description: "Effective Florisoft remote operation duration excluding explicit user interaction waits.",
    unit: "ms",
  })
  const count = meter?.createCounter("florisoft.remote.operation.count", {
    description: "Florisoft remote operation completions by kind and outcome.",
  })

  return {
    logger,
    loggerProvider,
    tracer,
    traceProvider,
    meterProvider,
    recordOperation(event) {
      const metricAttributes = {
        "florisoft.remote.kind": event.kind,
        "florisoft.remote.name": event.name,
        "florisoft.remote.outcome": event.outcome,
        ...(event.attributes["error.type"] == null ? {} : { "error.type": event.attributes["error.type"] }),
      }
      durationHistogram?.record(event.effectiveDurationMs, metricAttributes)
      count?.add(1, metricAttributes)
    },
    startRequest(kind, name, method, requestHeaders) {
      if (tracer == null || requestHeaders.has("traceparent")) return undefined
      const span = tracer.startSpan(`HTTP ${method.toUpperCase()}`, {
        kind: apiModule.SpanKind.CLIENT,
        attributes: {
          "http.request.method": method.toUpperCase(),
          "florisoft.remote.kind": kind,
          "florisoft.remote.name": name,
        },
      })
      injectW3cTraceContext(span, requestHeaders)
      return createRemoteRequest(span, apiModule.SpanStatusCode)
    },
  }
}

function createRemoteRequest(
  span: Span,
  spanStatusCode: typeof import("@opentelemetry/api").SpanStatusCode
): RemoteTelemetryRequest {
  let ended = false
  return {
    end(outcome, status) {
      if (ended) return
      ended = true
      if (status != null) span.setAttribute("http.response.status_code", status)
      span.setStatus({
        code:
          outcome === "error" ? spanStatusCode.ERROR : outcome === "success" ? spanStatusCode.OK : spanStatusCode.UNSET,
      })
      span.end()
    },
  }
}

function injectW3cTraceContext(span: Span, headers: Headers) {
  const context = span.spanContext()
  const traceFlags = context.traceFlags.toString(16).padStart(2, "0")
  headers.set("traceparent", `00-${context.traceId}-${context.spanId}-${traceFlags}`)
  const traceState = context.traceState?.serialize()
  if (traceState) headers.set("tracestate", traceState)
  else headers.delete("tracestate")
}

function installGlobalErrorCapture(telemetry: ApplicationTelemetry) {
  const onError = (event: ErrorEvent) => {
    const error = event.error instanceof Error ? event.error : new Error(event.message || "Uncaught browser error")
    telemetry.reportError(error, { "florisoft.error.source": "browser-error" })
  }
  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    telemetry.reportError(event.reason, { "florisoft.error.source": "unhandled-rejection" })
  }
  window.addEventListener("error", onError)
  window.addEventListener("unhandledrejection", onUnhandledRejection)
  return () => {
    window.removeEventListener("error", onError)
    window.removeEventListener("unhandledrejection", onUnhandledRejection)
  }
}

function normalizeSlowThreshold(value: number | false | undefined, fallback: number) {
  if (value === false) return false
  if (value == null) return fallback
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError("Telemetry slow-operation thresholds must be finite non-negative numbers or false.")
  }
  return value
}

function normalizeOtlpBaseUrl(baseUrl: string) {
  const value = baseUrl.trim()
  if (value.length === 0) throw new Error("Telemetry OTLP baseUrl must not be empty.")
  if (/^https?:\/\//i.test(value)) return value.replace(/\/+$/, "")
  if (globalThis.location == null) {
    throw new Error("A relative telemetry OTLP baseUrl requires a browser location.")
  }
  return new URL(value, globalThis.location.origin).href.replace(/\/+$/, "")
}

function sanitizeAttributes(attributes?: TelemetryAttributes) {
  if (attributes == null) return undefined
  return Object.fromEntries(
    Object.entries(attributes).filter((entry): entry is [string, TelemetryAttributeValue] => entry[1] !== undefined)
  )
}

function formatConsoleAttributes(attributes?: TelemetryAttributes) {
  const sanitized = sanitizeAttributes(attributes)
  if (sanitized == null) return undefined
  return Object.fromEntries(Object.entries(sanitized).map(([key, value]) => [key, parseStructuredConsoleValue(value)]))
}

function parseStructuredConsoleValue(value: TelemetryAttributeValue): unknown {
  if (typeof value !== "string") return value
  const trimmed = value.trim()
  if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) return value
  try {
    return JSON.parse(trimmed) as unknown
  } catch {
    return value
  }
}

function describeError(error: unknown): TelemetryAttributes {
  if (!(error instanceof Error)) {
    return {
      "exception.type": typeof error,
      "exception.message": "A non-Error value was thrown.",
    }
  }
  return {
    "exception.type": error.name,
    "exception.message": error.message,
    ...(error.stack == null ? {} : { "exception.stacktrace": error.stack }),
  }
}

function describeErrorType(error: unknown): TelemetryAttributes {
  return { "error.type": error instanceof Error ? error.name : typeof error }
}

function now() {
  return globalThis.performance?.now() ?? Date.now()
}
